/**
 * Quiet shimmer placeholder — the base unit for skeleton loading states.
 * Renders as a single filled bar; width/height come from `style` so callers
 * shape it to whatever's loading (a text line, a stat value, a table cell).
 * All the paint (fill, sweep, radius, reduced-motion fallback) lives in the
 * SKELETON section of index.css — this component is just the marked-up
 * element.
 *
 * A `<span>`, not a `<div>` — skeletons sometimes stand in for inline text
 * (a page subtitle, an `<h1>`), and a block element there is invalid HTML
 * inside a `<p>`. `.skeleton` sets `display: inline-block` so it still sizes
 * and lays out like a bar everywhere else (flex rows, grid cells, table
 * cells).
 */
export function Skeleton({ style, className }: { style?: React.CSSProperties; className?: string }) {
  return <span aria-hidden className={`skeleton${className ? ` ${className}` : ''}`} style={style} />;
}

/**
 * List/table-row shaped stack — two bars per row (a 60%-wide "title" over a
 * 35%-wide "sub"), spaced at the same row-height rhythm as `.inbox-row` and
 * similar list panes. The convenience shape most master-list loading
 * branches want; reach for bare `<Skeleton>` when a surface doesn't match
 * this two-line row shape (a table row's per-column cells, a stat tile).
 */
export function SkeletonRows({ rows = 5 }: { rows?: number }) {
  return (
    <div className="skeleton-rows" aria-hidden>
      {Array.from({ length: rows }).map((_, i) => (
        <div className="skeleton-row" key={i}>
          <Skeleton className="skeleton-row-title" />
          <Skeleton className="skeleton-row-sub" />
        </div>
      ))}
    </div>
  );
}
